import { XIcon, BuildingOfficeIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';

interface RecipientChipProps {
  email: string;
  onRemove: () => void;
  /**
   * Mark a recipient that isn't part of the organization (e.g. a free-typed
   * external address). Renders an amber pill with a "not in your organization"
   * badge, mirroring the Gmail external-recipient cue.
   */
  external?: boolean;
}

export function RecipientChip({
  email,
  onRemove,
  external = false,
}: RecipientChipProps) {
  const gt = useGT();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border py-0.5 pr-1.5 text-xs',
        external
          ? 'border-amber-400/70 bg-amber-100 pl-1 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100'
          : 'bg-muted/40 pl-2',
      )}
      title={
        external
          ? gt('Not in your organization', {
              context: 'external recipient chip tooltip',
            })
          : undefined
      }
    >
      {external && (
        <span
          aria-hidden
          className="inline-flex size-4 items-center justify-center rounded-full bg-amber-400 text-amber-950"
        >
          <BuildingOfficeIcon className="size-2.5" />
        </span>
      )}
      <span>{email}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={gt('Remove {email}', {
          context: 'remove recipient chip button',
          email,
        })}
        className={cn(
          'cursor-pointer',
          external
            ? 'text-amber-700 hover:text-amber-950 dark:text-amber-300 dark:hover:text-amber-100'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}
